import type {
  Entry,
  EntryRow,
  Project,
  Settings,
  Share,
  ShareConfig,
  ShareColumn,
  Summary,
  Totals,
} from '../../shared/types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json', ...init?.headers } : init?.headers,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, body?.error ?? `Request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

const qs = (params: Record<string, string | string[] | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
    else if (value) search.set(key, value);
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export interface EntryQuery {
  from: string;
  to: string;
  projectIds?: string[];
  types?: string[];
}

export interface PublicPayload {
  label: string;
  columns: ShareColumn[];
  range: { from: string; to: string };
  rows: Record<string, unknown>[];
  totals: { minutes: number; entry_count: number; amount_inr?: number } | null;
}

export const api = {
  me: () => request<{ signedIn: boolean }>('/auth/me'),
  login: (password: string) =>
    request<{ signedIn: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  logout: () => request<{ signedIn: boolean }>('/auth/logout', { method: 'POST' }),

  listProjects: () => request<Project[]>('/projects'),
  createProject: (input: Partial<Project>) =>
    request<Project>('/projects', { method: 'POST', body: JSON.stringify(input) }),
  updateProject: (id: string, input: Partial<Project>) =>
    request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteProject: (id: string) => request<{ deleted: true }>(`/projects/${id}`, { method: 'DELETE' }),

  listEntries: (q: EntryQuery) =>
    request<{ entries: EntryRow[]; totals: Totals }>(
      `/entries${qs({ from: q.from, to: q.to, projectId: q.projectIds, type: q.types })}`,
    ),
  createEntry: (input: Partial<Entry>) =>
    request<Entry>('/entries', { method: 'POST', body: JSON.stringify(input) }),
  updateEntry: (id: string, input: Partial<Entry>) =>
    request<Entry>(`/entries/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteEntry: (id: string) => request<{ deleted: true }>(`/entries/${id}`, { method: 'DELETE' }),

  summary: (from: string, to: string, groupBy: 'day' | 'week' | 'month', projectIds?: string[]) =>
    request<Summary>(`/summary${qs({ from, to, groupBy, projectId: projectIds })}`),

  settings: () => request<Settings>('/settings'),
  saveSettings: (input: Partial<Settings>) =>
    request<Settings>('/settings', { method: 'PUT', body: JSON.stringify(input) }),

  listShares: () => request<Share[]>('/shares'),
  createShare: (label: string, config: ShareConfig, expires_at: string | null) =>
    request<Share>('/shares', { method: 'POST', body: JSON.stringify({ label, config, expires_at }) }),
  revokeShare: (id: string) => request<{ revoked: true }>(`/shares/${id}`, { method: 'DELETE' }),

  publicView: (token: string) => request<PublicPayload>(`/public/${token}`),
};
